import { Navigate, Outlet, type RouteObject } from "react-router-dom"

import { useAppContext } from "@contexts/app_context"
import { CustomerPageContextProvider } from "@contexts/customer"
import { AiAnswersPageContextProvider } from "@contexts/main/ai_answers_page_context"
import { AiEvalMocksPageContextProvider } from "@contexts/main/ai_eval_mocks_page_context"
import { AiEvalsPageContextProvider } from "@contexts/main/ai_evals_page_context"
import { AiHubContextProvider } from "@contexts/main/ai_hub_context"
import AiHubPage from "@pages/main/ai_hub"
import AiOperatorPage from "@pages/main/ai_operator"
import { CallLogsPageContextProvider } from "@contexts/main/call_logs_page_context"
import { ChangelogPageContextProvider } from "@contexts/main/changelog_page_context"
import { CampaignLinksPageContextProvider } from "@contexts/main/campaign_links_page_context"
import { CouponsPageContextProvider } from "@contexts/main/coupons_page_context"
import { DashboardPageContextProvider } from "@contexts/main/dashboard_page_context"
import { ScriptsPageContextProvider } from "@contexts/main/scripts_page_context"
import { UserPageContextProvider } from "@contexts/main/users_page_context"
import { MobileAppVersionPageContextProvider } from "@contexts/main/mobile_app_version_page_context"
import { LiveChatWidgetPageContextProvider } from "@contexts/main/live_chat_widget_page_context"
import { WhatsappPageContextProvider } from "@contexts/main/whatsapp_page_context"
import MainLayout from "@layouts/main_layout"
import CustomerInfosPageHeader from "@pages/customer/infos/header"
import InfosTabs from "@pages/customer/infos/tabs/infosTabs"
import CustomerPage from "@pages/customer/list"
import CustomerPageHeader from "@pages/customer/list/header"
import CustomerPageLeftDrawer from "@pages/customer/list/left_drawer"
import CustomerPaymentsPage from "@pages/customer/payments"
import ErrorPage from "@pages/error"
import AiAnswersPage from "@pages/main/ai_answers"
import AiAnalysisTestPage from "@pages/main/ai_analysis_test"
import AiSimulationHubPage from "@pages/main/ai_simulation"
import AiEvalMocksPage from "@pages/main/ai_eval_mocks"
import AiEnginePage from "@pages/main/ai_engine"
import AiHelpPage from "@pages/main/ai_help"
import AiEvalsPage from "@pages/main/ai_evals"
import AiFlowPage from "@pages/main/ai_flow"
import CallLogsPage from "@pages/main/call_logs"
import CallLogsPageLeftDrawer from "@pages/main/call_logs/left_drawer"
import ChangelogPage from "@pages/main/changelog"
import ChangelogPageLeftDrawer from "@pages/main/changelog/left_drawer"
import CampaignLinksPage from "@pages/main/campaign_links"
import CouponsPage from "@pages/main/coupons"
import CouponsPageHeader from "@pages/main/coupons/header"
import CouponsPageLeftDrawer from "@pages/main/coupons/left_drawer"
import DashboardPage from "@pages/main/dashboard"
import ScriptsPage from "@pages/main/scripts"
import ScriptsPageHeader from "@pages/main/scripts/header"
import ScriptsPageLeftDrawer from "@pages/main/scripts/left_drawer"
import MessageTest from "@pages/main/scripts/message_test"
import MigrationsPage from "@pages/main/scripts/migrations"
import PromptsPage from "@pages/main/scripts/prompts"
import UsersPage from "@pages/main/users"
import MobileAppVersionPage from "@pages/main/mobile_app_version"
import LiveChatWidgetPage from "@pages/main/live_chat_widget"
import ContactsPageHeader from "@pages/main/users/header"
import ContactsPageLeftDrawer from "@pages/main/users/left_drawer"
import WhatsappBuilderPage from "@pages/main/whatsapp_api/builder"
import WhatsappBuilderPageHeader from "@pages/main/whatsapp_api/builder/header"
import WhatsappPage from "@pages/main/whatsapp_api/list"
import WhatsappPageHeader from "@pages/main/whatsapp_api/list/header"
import WhatsappPageLeftDrawer from "@pages/main/whatsapp_api/list/left_drawer"
import { RoleTypes } from "__generated__/graphql"
import { EmailLogsPageContextProvider } from "@contexts/main/email_logs_page_context"
import EmailLogsPageLeftDrawer from "@pages/main/email_logs/left_drawer"
import EmailLogsPage from "@pages/main/email_logs"
import { MarketplaceOrdersPageContextProvider } from "@contexts/main/marketplace_orders_page_context"
import MarketplaceOrdersPageLeftDrawer from "@pages/main/marketplace_orders/left_drawer"
import MarketplaceOrdersPage from "@pages/main/marketplace_orders"

const ProtectedRoute = ({
  element,
  roles
}: {
  element: JSX.Element
  roles?: RoleTypes[]
}) => {
  const { me } = useAppContext()

  const userRole = me?.role
  if (!userRole) {
    return <ErrorPage />
  }

  if (!roles || roles.includes(userRole)) {
    return element
  }

  return <ErrorPage />
}

const mainNavigation: RouteObject[] = [
  {
    path: "/",
    element: <ErrorPage />,
    errorElement: <ErrorPage />
  },
  {
    path: "/dashboard",
    element: (
      <MainLayout
        bgColor="secondary"
        ContextProvider={DashboardPageContextProvider}
      >
        <DashboardPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  /* {
    path: "/users",
    element: (
      <MainLayout
        ContextProvider={UserPageContextProvider}
        HeaderComponent={ContactsPageHeader}
        LeftDrawer={ContactsPageLeftDrawer}
      >
        <UsersPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  }, */
  {
    path: "/users",
    element: (
      <ProtectedRoute
        roles={[RoleTypes.Admin]}
        element={
          <MainLayout
            ContextProvider={UserPageContextProvider}
            HeaderComponent={ContactsPageHeader}
            LeftDrawer={ContactsPageLeftDrawer}
          >
            <UsersPage />
          </MainLayout>
        }
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/mobile-app-version",
    element: (
      <ProtectedRoute
        roles={[RoleTypes.Admin]}
        element={
          <MainLayout ContextProvider={MobileAppVersionPageContextProvider}>
            <MobileAppVersionPage />
          </MainLayout>
        }
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/livechat-widget",
    element: (
      <ProtectedRoute
        roles={[RoleTypes.Admin]}
        element={
          <MainLayout ContextProvider={LiveChatWidgetPageContextProvider}>
            <LiveChatWidgetPage />
          </MainLayout>
        }
      />
    ),
    errorElement: <ErrorPage />
  },
  /* {
    path: "/scripts",
    element: (
      <MainLayout
        ContextProvider={ScriptsPageContextProvider}
        HeaderComponent={ScriptsPageHeader}
        LeftDrawer={ScriptsPageLeftDrawer}
      >
        <Outlet />
      </MainLayout>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <ScriptsPage /> },
      { path: "prompts", element: <PromptsPage /> }
    ]
  }, */
  {
    path: "/scripts",
    element: (
      <ProtectedRoute
        roles={[RoleTypes.Admin]}
        element={
          <MainLayout
            ContextProvider={ScriptsPageContextProvider}
            HeaderComponent={ScriptsPageHeader}
            LeftDrawer={ScriptsPageLeftDrawer}
          >
            <Outlet />
          </MainLayout>
        }
      />
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <ScriptsPage /> },
      { path: "message-test", element: <MessageTest /> },
      { path: "migrations", element: <MigrationsPage /> }
    ]
  },
  {
    path: "/ai",
    element: (
      <AiHubContextProvider>
        <MainLayout>
          <AiHubPage />
        </MainLayout>
      </AiHubContextProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to="logs" replace /> },
      {
        path: "flow",
        element: <AiFlowPage />
      },
      {
        path: "prompts",
        element: (
          <ProtectedRoute
            roles={[RoleTypes.Admin]}
            element={
              <ScriptsPageContextProvider>
                <PromptsPage />
              </ScriptsPageContextProvider>
            }
          />
        )
      },
      {
        path: "logs",
        element: (
          <AiAnswersPageContextProvider>
            <AiAnswersPage />
          </AiAnswersPageContextProvider>
        )
      },
      {
        path: "logs/:conversationId",
        element: (
          <AiAnswersPageContextProvider>
            <AiAnswersPage />
          </AiAnswersPageContextProvider>
        )
      },
      {
        path: "operator",
        element: <AiOperatorPage />
      },
      {
        path: "operator/:runId",
        element: <AiOperatorPage />
      },
      {
        path: "simulation",
        element: <AiSimulationHubPage />
      },
      {
        path: "engine",
        element: (
          <ProtectedRoute
            roles={[RoleTypes.Admin]}
            element={<AiEnginePage />}
          />
        )
      },
      {
        path: "help",
        element: (
          <ProtectedRoute roles={[RoleTypes.Admin]} element={<AiHelpPage />} />
        )
      },
      {
        path: "evals",
        element: (
          <ProtectedRoute
            roles={[RoleTypes.Admin]}
            element={
              <AiEvalsPageContextProvider>
                <AiEvalsPage />
              </AiEvalsPageContextProvider>
            }
          />
        )
      },
      {
        path: "eval-mocks",
        element: (
          <ProtectedRoute
            roles={[RoleTypes.Admin]}
            element={
              <AiEvalMocksPageContextProvider>
                <AiEvalMocksPage />
              </AiEvalMocksPageContextProvider>
            }
          />
        )
      },
      {
        path: "analysis-test",
        element: (
          <ProtectedRoute
            roles={[RoleTypes.Admin]}
            element={<AiAnalysisTestPage />}
          />
        )
      }
    ]
  },
  { path: "/ai-answers", element: <Navigate to="/ai/logs" replace /> },
  { path: "/ai-evals", element: <Navigate to="/ai/evals" replace /> },
  { path: "/ai-eval-mocks", element: <Navigate to="/ai/eval-mocks" replace /> },
  { path: "/scripts/prompts", element: <Navigate to="/ai/prompts" replace /> },
  {
    path: "/call-logs",
    element: (
      <MainLayout
        ContextProvider={CallLogsPageContextProvider}
        LeftDrawer={CallLogsPageLeftDrawer}
      >
        <CallLogsPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/email-logs",
    element: (
      <MainLayout
        ContextProvider={EmailLogsPageContextProvider}
        LeftDrawer={EmailLogsPageLeftDrawer}
      >
        <EmailLogsPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/marketplace-orders",
    element: (
      <MainLayout
        ContextProvider={MarketplaceOrdersPageContextProvider}
        LeftDrawer={MarketplaceOrdersPageLeftDrawer}
      >
        <MarketplaceOrdersPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/campaign-links",
    element: (
      <MainLayout ContextProvider={CampaignLinksPageContextProvider}>
        <CampaignLinksPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/coupons",
    element: (
      <MainLayout
        ContextProvider={CouponsPageContextProvider}
        HeaderComponent={CouponsPageHeader}
        LeftDrawer={CouponsPageLeftDrawer}
      >
        <CouponsPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/whatsapp",
    element: (
      <MainLayout
        ContextProvider={WhatsappPageContextProvider}
        HeaderComponent={WhatsappPageHeader}
        LeftDrawer={WhatsappPageLeftDrawer}
      >
        <WhatsappPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/whatsapp/:id",
    element: (
      <MainLayout
        ContextProvider={WhatsappPageContextProvider}
        HeaderComponent={WhatsappBuilderPageHeader}
        LeftDrawer={WhatsappPageLeftDrawer}
      >
        <WhatsappBuilderPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/customers",
    element: (
      <MainLayout
        ContextProvider={CustomerPageContextProvider}
        HeaderComponent={CustomerPageHeader}
        LeftDrawer={CustomerPageLeftDrawer}
      >
        <CustomerPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/customers/payments",
    element: (
      <MainLayout
        ContextProvider={CustomerPageContextProvider}
        HeaderComponent={CustomerPageHeader}
        LeftDrawer={CustomerPageLeftDrawer}
      >
        <CustomerPaymentsPage />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/customers/:id",
    element: (
      <MainLayout
        ContextProvider={CustomerPageContextProvider}
        HeaderComponent={CustomerInfosPageHeader}
        LeftDrawer={CustomerPageLeftDrawer}
      >
        <InfosTabs />
      </MainLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/changelog/:section?",
    element: (
      <ProtectedRoute
        roles={[RoleTypes.Admin]}
        element={
          <MainLayout
            ContextProvider={ChangelogPageContextProvider}
            LeftDrawer={ChangelogPageLeftDrawer}
          >
            <ChangelogPage />
          </MainLayout>
        }
      />
    ),
    errorElement: <ErrorPage />
  }
]

export default mainNavigation
